/**
 * File: assets/basari-wishlist-monk.js
 * Purpose: Forward the two Basari PDP hearts to the existing Wishlist Monk
 *          product button, then mirror mrw-wishlist-button.isActive.
 * Scope: default eyewear PDP only. Contact lens, cards, and other templates
 *        are not initialized.
 *
 * Sync approach:
 * isActive is a JS property, not a DOM attribute, so it cannot be observed
 * directly. Wishlist Monk also updates the heart SVG fill inside the same
 * element when state changes, including after variant switches. A
 * MutationObserver on that button reads isActive whenever its subtree changes.
 * A short bounded recheck (400ms, at most 8 times) runs only after the button
 * appears, after a Basari heart click, and after basari:variant:change, in
 * case the property updates before the SVG mutation. It is not a standing poll.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-basari-product]:not([data-basari-contact-lens])';
  var HEART_SELECTOR =
    '.basari-gallery__action--wishlist [data-basari-wishlist-button], .basari-product-actions [data-basari-wishlist]';
  var MONK_SELECTOR = '.basari-product-actions mrw-wishlist-button';
  var READY_ATTR = 'data-basari-wishlist-monk-ready';
  var SYNC_INTERVAL_MS = 400;
  var SYNC_MAX_CHECKS = 8;
  var PENDING_CLICK_MS = 8000;

  function findMonkButton(root) {
    if (!root || !root.querySelector) return null;
    return root.querySelector(MONK_SELECTOR);
  }

  function getHearts(root) {
    if (!root || !root.querySelectorAll) return [];
    return root.querySelectorAll(HEART_SELECTOR);
  }

  function applyPressed(root, active) {
    var hearts = getHearts(root);
    var pressed = active ? 'true' : 'false';
    var i;

    for (i = 0; i < hearts.length; i += 1) {
      if (hearts[i].getAttribute('aria-pressed') !== pressed) {
        hearts[i].setAttribute('aria-pressed', pressed);
      }
    }
  }

  function syncFrom(root, button) {
    if (!button || !button.isConnected) return;
    try {
      applyPressed(root, button.isActive === true);
    } catch (error) {
      /* Leave the PDP usable if Monk state cannot be read. */
    }
  }

  function concealMonkButton(button) {
    var focusable;
    var i;

    if (!button || button.getAttribute('data-basari-monk-concealed') === 'true') return;

    button.setAttribute('data-basari-monk-concealed', 'true');
    button.setAttribute('aria-hidden', 'true');

    focusable = button.querySelectorAll('button, [tabindex]');
    for (i = 0; i < focusable.length; i += 1) {
      focusable[i].setAttribute('tabindex', '-1');
    }
  }

  function initRoot(root) {
    var actions;
    var discoveryObserver = null;
    var stateObserver = null;
    var removalObserver = null;
    var boundButton = null;
    var syncTimer = null;
    var pendingClick = false;
    var pendingTimer = null;

    if (!root || root.getAttribute(READY_ATTR) === 'true') return;
    if (root.hasAttribute('data-basari-contact-lens')) return;

    root.setAttribute(READY_ATTR, 'true');

    function clearSyncTimer() {
      if (!syncTimer) return;
      clearInterval(syncTimer);
      syncTimer = null;
    }

    function scheduleSync() {
      var checks = 0;

      syncFrom(root, boundButton || findMonkButton(root));
      clearSyncTimer();
      syncTimer = setInterval(function () {
        checks += 1;
        syncFrom(root, boundButton && boundButton.isConnected ? boundButton : findMonkButton(root));
        if (checks >= SYNC_MAX_CHECKS) clearSyncTimer();
      }, SYNC_INTERVAL_MS);
    }

    function clearPendingClick() {
      pendingClick = false;
      if (!pendingTimer) return;
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }

    function queueClick() {
      pendingClick = true;
      if (pendingTimer) clearTimeout(pendingTimer);
      pendingTimer = setTimeout(clearPendingClick, PENDING_CLICK_MS);
    }

    function triggerMonk(button) {
      if (!button) return false;
      try {
        button.click();
      } catch (error) {
        return false;
      }
      scheduleSync();
      return true;
    }

    function stopStateWatch() {
      if (stateObserver) {
        stateObserver.disconnect();
        stateObserver = null;
      }
      if (removalObserver) {
        removalObserver.disconnect();
        removalObserver = null;
      }
    }

    function startDiscovery() {
      if (discoveryObserver || !actions) return;

      discoveryObserver = new MutationObserver(function () {
        var button = findMonkButton(root);
        if (button) bindMonkButton(button);
      });

      discoveryObserver.observe(actions, { childList: true, subtree: true });
    }

    function bindMonkButton(button) {
      var shouldClick = pendingClick;

      if (!button) return;
      if (button === boundButton) {
        syncFrom(root, button);
        if (shouldClick) {
          clearPendingClick();
          triggerMonk(button);
        }
        return;
      }

      stopStateWatch();
      boundButton = button;
      concealMonkButton(button);

      if (discoveryObserver) {
        discoveryObserver.disconnect();
        discoveryObserver = null;
      }

      stateObserver = new MutationObserver(function () {
        syncFrom(root, boundButton);
      });
      stateObserver.observe(button, {
        attributes: true,
        childList: true,
        subtree: true
      });

      if (button.parentElement) {
        removalObserver = new MutationObserver(function () {
          if (boundButton && boundButton.isConnected) return;
          boundButton = null;
          stopStateWatch();
          startDiscovery();
          bindMonkButton(findMonkButton(root));
        });
        removalObserver.observe(button.parentElement, { childList: true });
      }

      scheduleSync();

      if (shouldClick) {
        clearPendingClick();
        triggerMonk(button);
      }
    }

    actions = root.querySelector('.basari-product-actions');

    root.addEventListener('click', function (event) {
      var target = event.target;
      var heart;
      var button;

      if (!target || !target.closest) return;
      heart = target.closest(HEART_SELECTOR);
      if (!heart || !root.contains(heart)) return;

      event.preventDefault();
      button = boundButton && boundButton.isConnected ? boundButton : findMonkButton(root);

      if (!button) {
        queueClick();
        startDiscovery();
        return;
      }

      triggerMonk(button);
    });

    root.addEventListener('basari:variant:change', function () {
      var button = boundButton && boundButton.isConnected ? boundButton : findMonkButton(root);
      if (!button) return;
      bindMonkButton(button);
      scheduleSync();
    });

    bindMonkButton(findMonkButton(root));
    if (!boundButton) startDiscovery();
  }

  function initAll(scope) {
    var context = scope || document;
    var roots = context.querySelectorAll ? context.querySelectorAll(ROOT_SELECTOR) : [];
    var i;

    for (i = 0; i < roots.length; i += 1) {
      initRoot(roots[i]);
    }

    if (context.matches && context.matches(ROOT_SELECTOR)) {
      initRoot(context);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) initAll(event.target);
  });
})();
